<template>
  <div
    class="min-h-screen rounded-3xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-[#050816] text-slate-900 dark:text-slate-50 font-sans shadow-apple selection:bg-sky-500/20">
    <div class="fixed inset-0 -z-10 overflow-hidden">
      <div class="aurora-bg"></div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8 sm:space-10">
      <header class="space-y-4 sm:space-y-5 animate-fadeInUp" style="animation-delay: 70ms;">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="space-y-2">
            <div
              class="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">
              <span class="inline-block h-1.5 w-1.5 rounded-full bg-sky-500"></span>
              Palette workspace
            </div>
            <div class="space-y-1">
              <h1 class="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
                Hello, <span
                  class="font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-500 to-cyan-400">{{ firstName
                  }}</span>
              </h1>
              <p class="text-sm sm:text-base text-slate-600 dark:text-slate-400 max-w-xl">
                A focused dashboard to jump back into palette projects and manage your asset library.
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2 sm:gap-3 justify-start sm:justify-end">

            <router-link to="/dashboard/hub"
              class="inline-flex items-center gap-1.5 rounded-2xl px-3.5 py-2 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-100 border border-slate-200/70 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/80 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all hover:text-sky-600 dark:hover:text-sky-400 group">
              <span
                class="material-symbols-outlined text-[18px] group-hover:rotate-12 transition-transform duration-300">
                school
              </span>
              <span class="hidden sm:inline">Hub</span>
            </router-link>

            <button type="button" @click="openExplorer"
              class="inline-flex items-center gap-1.5 rounded-2xl px-3.5 py-2 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-100 border border-slate-200/70 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/80 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all">
              <span class="material-symbols-outlined text-[18px]">
                gallery_thumbnail
              </span>
              Library explorer
            </button>

            <button type="button" @click="openCreateProject"
              class="inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white bg-slate-900 dark:bg-sky-500 shadow-lg shadow-slate-900/20 dark:shadow-sky-500/30 hover:-translate-y-0.5 hover:shadow-xl transition-all">
              <span class="material-symbols-outlined text-[20px]">
                add_circle
              </span>
              New palette project
            </button>
          </div>
        </div>

        <div
          class="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-white/80 dark:bg-slate-950/70 backdrop-blur-xl px-3.5 py-3.5 sm:px-4 sm:py-4 shadow-sm shadow-slate-900/10 dark:shadow-black/30 animate-fadeInUp"
          style="animation-delay: 120ms;">
          <div class="flex items-center gap-3">
            <div
              class="h-9 w-9 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-300 flex items-center justify-center">
              <span class="material-symbols-outlined text-[20px]">
                view_carousel
              </span>
            </div>
            <div class="space-y-0.5">
              <p class="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Projects
              </p>
              <p class="text-base sm:text-lg font-semibold tracking-tight">
                {{ projects.length }}
              </p>
              <p class="text-[11px] text-slate-500 dark:text-slate-400">
                Active palette spaces
              </p>
            </div>
          </div>

          <div
            class="flex items-center gap-3 border-t sm:border-t-0 sm:border-l border-slate-200/80 dark:border-slate-800/80 pt-3 sm:pt-0 sm:pl-4">
            <div
              class="h-9 w-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 flex items-center justify-center">
              <span class="material-symbols-outlined text-[20px]">
                folder_open
              </span>
            </div>
            <div class="space-y-0.5">
              <p class="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Asset folders
              </p>
              <p class="text-base sm:text-lg font-semibold tracking-tight">
                {{ folders.length || (loading.folders ? '—' : 0) }}
              </p>
              <p class="text-[11px] text-slate-500 dark:text-slate-400">
                Grouped by exports and uploads
              </p>
            </div>
          </div>

          <div
            class="flex items-center gap-3 border-t sm:border-t-0 sm:border-l border-slate-200/80 dark:border-slate-800/80 pt-3 sm:pt-0 sm:pl-4">
            <div
              class="h-9 w-9 rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-300 flex items-center justify-center">
              <span class="material-symbols-outlined text-[20px]">
                schedule
              </span>
            </div>
            <div class="space-y-0.5">
              <p class="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Activity
              </p>
              <p class="text-base sm:text-lg font-semibold tracking-tight">
                {{ heroProjects.length ? 'Recently updated' : 'Waiting for projects' }}
              </p>
              <p class="text-[11px] text-slate-500 dark:text-slate-400">
                Keep iterating on your color work
              </p>
            </div>
          </div>
        </div>
      </header>

      <main class="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-7 items-start animate-fadeInUp"
        style="animation-delay: 180ms;">
        <section
          class="xl:col-span-7 space-y-4 rounded-3xl border border-slate-200/80 dark:border-slate-800/80 bg-white/90 dark:bg-slate-950/80 backdrop-blur-xl p-4 sm:p-5 shadow-lg shadow-slate-900/10 dark:shadow-black/40">
          <div class="flex items-start justify-between gap-3 flex-wrap">
            <div class="space-y-1">
              <div class="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <span class="material-symbols-outlined text-[18px]">
                  palette
                </span>
                <h2 class="text-base sm:text-lg font-semibold">
                  Recent palette projects
                </h2>
              </div>
              <p class="text-xs sm:text-[13px] text-slate-500 dark:text-slate-400">
                Quickly reopen your most recently updated projects.
              </p>
            </div>
            <button type="button" @click="openCreateProject"
              class="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/80 px-3 py-1.5 text-[11px] sm:text-xs font-medium text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900">
              <span class="material-symbols-outlined text-[16px]">
                bolt
              </span>
              Quick create
            </button>
          </div>

          <div v-if="heroProjects.length" class="space-y-3.5">
            <div class="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>
                Showing up to {{ heroProjects.length }} projects
              </span>
              <div class="flex items-center gap-1.5">
                <button type="button" class="icon-ghost h-7 w-7" @click="scrollHero(-1)">
                  <span class="material-symbols-outlined text-[18px]">
                    chevron_left
                  </span>
                </button>
                <button type="button" class="icon-ghost h-7 w-7" @click="scrollHero(1)">
                  <span class="material-symbols-outlined text-[18px]">
                    chevron_right
                  </span>
                </button>
              </div>
            </div>

            <div ref="heroScroller"
              class="flex gap-4 overflow-x-auto pb-1 pt-1 px-0.5 snap-x snap-mandatory scroll-smooth fancy-scroll">
              <div class="snap-start flex-shrink-0 w-[240px] sm:w-[260px]">
                <button type="button" @click="openCreateProject"
                  class="group relative w-full h-40 rounded-2xl border border-dashed border-slate-300/80 dark:border-slate-700/80 bg-slate-50/90 dark:bg-slate-900/70 hover:border-sky-400/80 hover:bg-slate-100/95 dark:hover:bg-slate-900 transition-all flex flex-col items-center justify-center gap-2.5">
                  <div
                    class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-300 group-hover:bg-sky-500/15">
                    <span class="material-symbols-outlined text-2xl">
                      add
                    </span>
                  </div>
                  <div class="space-y-0.5 text-center">
                    <p class="text-sm font-semibold tracking-tight">
                      Create new project
                    </p>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400 max-w-[15rem] mx-auto">
                      Start a new palette exploration with custom notes and exports.
                    </p>
                  </div>
                </button>
              </div>

              <div v-for="p in heroProjects" :key="p.id"
                class="snap-start flex-shrink-0 w-[240px] sm:w-[260px] card-glow-container">
                <button type="button" @click="goOpenProject(p.id)" class="card-glow group"
                  :style="{ '--glow-color': p.glow }">
                  <div class="card-bg" :class="p.bg"></div>
                  <div class="card-content">
                    <div class="flex flex-col gap-1.5 min-w-0">
                      <div
                        class="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">
                        <span class="h-1 w-1 rounded-full bg-emerald-300"></span>
                        Palette project
                      </div>
                      <h3 class="mt-1 text-base sm:text-[17px] font-semibold leading-tight truncate">
                        {{ p.name }}
                      </h3>
                      <p class="text-[11px] text-white/75">
                        {{ p.count }} colorsets · Updated {{ p.when }}
                      </p>
                    </div>
                    <div class="mt-3 flex items-center justify-between gap-2">
                      <div class="flex -space-x-1">
                        <span v-for="c in p.previewColors || []" :key="c"
                          class="h-4 w-4 rounded-full ring-1 ring-white/60 shadow-sm"
                          :style="{ backgroundColor: c }"></span>
                      </div>
                      <div class="flex items-center gap-1 text-[11px] text-white/80">
                        <span>Open</span>
                        <span
                          class="material-symbols-outlined text-[18px] opacity-70 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-1 transition-all">
                          arrow_forward
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div v-else
            class="mt-2 rounded-2xl border border-dashed border-slate-300/80 dark:border-slate-700/80 bg-slate-50/90 dark:bg-slate-950/80 px-5 py-6 text-center space-y-2.5">
            <div
              class="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-500/10 text-sky-600 dark:text-sky-300">
              <span class="material-symbols-outlined text-[22px]">
                palette
              </span>
            </div>
            <p class="text-sm font-medium">
              No palette projects yet
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
              Create your first palette project to start exploring color systems, exports and combinations.
            </p>
            <div class="flex items-center justify-center gap-2 pt-1">
              <button type="button" @click="openCreateProject"
                class="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium text-white bg-slate-900 dark:bg-sky-500 hover:-translate-y-0.5 hover:shadow-md transition-all">
                <span class="material-symbols-outlined text-[16px]">
                  add
                </span>
                Create project
              </button>
              <button type="button" @click="openExplorer"
                class="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-950/80 text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-900">
                <span class="material-symbols-outlined text-[16px]">
                  image
                </span>
                Open library
              </button>
            </div>
          </div>
        </section>

        <aside
          class="xl:col-span-5 space-y-4 rounded-3xl border border-slate-200/80 dark:border-slate-800/80 bg-white/90 dark:bg-slate-950/80 backdrop-blur-xl p-4 sm:p-5 shadow-lg shadow-slate-900/10 dark:shadow-black/40">
          <div class="flex items-start justify-between gap-3 flex-wrap">
            <div class="space-y-1">
              <div class="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <span class="material-symbols-outlined text-[18px]">
                  folder_special
                </span>
                <h2 class="text-base sm:text-lg font-semibold">
                  Asset folders
                </h2>
              </div>
              <p class="text-xs sm:text-[13px] text-slate-500 dark:text-slate-400">
                Groups of PNG exports, previews and reference files.
              </p>
            </div>
            <button type="button" @click="refreshFolders"
              class="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/80 px-3 py-1.5 text-[11px] sm:text-xs font-medium text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900">
              <span class="material-symbols-outlined text-[16px]">
                refresh
              </span>
              Refresh
            </button>
          </div>

          <div class="flex items-center justify-between text-[11px] mt-1.5">
            <span class="text-slate-500 dark:text-slate-400">
              {{ folders.length || (loading.folders ? '—' : 0) }} folder(s) · showing up to {{ displayedFolders.length
              || 0 }}
            </span>
            <span v-if="loading.folders" class="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400">
              <span class="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Loading
            </span>
          </div>

          <div class="mt-3 space-y-3">
            <div v-if="!folders.length && loading.folders" class="space-y-2.5">
              <div v-for="i in 3" :key="'sk-' + i"
                class="h-[72px] rounded-2xl bg-slate-100/90 dark:bg-slate-900/70 animate-pulse"></div>
            </div>

            <div v-else-if="displayedFolders.length" class="space-y-2.5">
              <div v-for="f in displayedFolders" :key="f.name"
                class="group flex items-center gap-3 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-white/90 dark:bg-slate-950/80 px-3.5 py-3 hover:border-sky-400/80 hover:bg-sky-50/60 dark:hover:bg-slate-900/90 transition-all cursor-pointer"
                @click="openFolder(f.name)">
                <div
                  class="relative h-12 w-12 rounded-xl overflow-hidden flex-shrink-0 ring-1 ring-slate-200/80 dark:ring-slate-800/80"
                  :style="{ background: folderPreviewGradient(f.name) }">
                  <div
                    class="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(255,255,255,0.9),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(15,23,42,0.4),transparent_55%)] mix-blend-soft-light opacity-80">
                  </div>
                </div>

                <div class="min-w-0 flex-1">
                  <p class="text-sm font-semibold truncate">
                    {{ f.name || 'Root' }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ f.count }} file{{ f.count === 1 ? '' : 's' }}
                  </p>
                </div>

                <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" title="Copy URLs" class="icon-ghost h-7 w-7"
                    @click.stop="copyFolderUrlsQuick(f.name)">
                    <span class="material-symbols-outlined text-[18px]">
                      content_copy
                    </span>
                  </button>
                  <button type="button" title="Open in gallery" class="icon-ghost h-7 w-7"
                    @click.stop="openFolderGallery(f.name)">
                    <span class="material-symbols-outlined text-[18px]">
                      widgets
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div v-else
              class="rounded-2xl border border-dashed border-slate-300/80 dark:border-slate-700/80 bg-slate-50/90 dark:bg-slate-950/80 px-4 py-5 text-center space-y-2.5">
              <p class="text-sm font-medium">
                No folders detected
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                Upload palette exports and previews; they will be automatically grouped into folders here.
              </p>
              <button type="button"
                class="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-900/80 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900"
                @click="openExplorer">
                <span class="material-symbols-outlined text-[16px]">
                  upload
                </span>
                Open uploader
              </button>
            </div>
          </div>

          <div class="pt-1 border-t border-slate-100/80 dark:border-slate-800/80 mt-3">
            <div class="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>Selected URLs</span>
              <span class="font-medium">{{ selectedUrls.length }}</span>
            </div>
            <div class="mt-2 flex items-center gap-2">
              <button type="button" class="chip flex-1 justify-center" @click="copySelectedUrls"
                :disabled="!selectedUrls.length">
                <span class="material-symbols-outlined text-[16px]">
                  link
                </span>
                Copy selected
              </button>
              <button type="button" class="chip flex-none" @click="clearSelection" :disabled="!selectedUrls.length">
                <span class="material-symbols-outlined text-[16px]">
                  close
                </span>
              </button>
            </div>
          </div>
        </aside>
      </main>
    </div>

    <transition name="modal">
      <div v-if="modals.folder" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/55 backdrop-blur-sm" @click="closeModals"></div>
        <div
          class="relative w-full max-w-5xl rounded-3xl overflow-hidden border border-slate-200/80 dark:border-slate-800/80 bg-slate-950/95 dark:bg-[#020617]/95 text-slate-50 shadow-2xl shadow-slate-950/80">
          <div class="px-5 sm:px-6 py-4 border-b border-slate-800/80 flex items-center justify-between gap-3 flex-wrap">
            <div class="space-y-1 min-w-0">
              <p class="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                Folder
              </p>
              <h3 class="text-sm sm:text-base font-semibold truncate">
                {{ activeFolder || 'Root' }}
              </h3>
            </div>
            <div class="flex items-center gap-1.5 flex-wrap justify-end">
              <button class="chip" type="button" @click="selectAllInFolder">
                <span class="material-symbols-outlined text-[16px]">
                  checklist
                </span>
                Select all
              </button>
              <button class="chip" type="button" @click="clearSelection">
                <span class="material-symbols-outlined text-[16px]">
                  deselect
                </span>
                Clear
              </button>
              <button class="chip" type="button" @click="copySelectedUrls">
                <span class="material-symbols-outlined text-[16px]">
                  content_copy
                </span>
                Copy URLs
              </button>
              <button class="chip hidden sm:inline-flex" type="button" @click="openFolderGallery(activeFolder)">
                <span class="material-symbols-outlined text-[16px]">
                  widgets
                </span>
                Open in gallery
              </button>
              <button class="chip" type="button" @click="loadFolderFiles(activeFolder)">
                <span class="material-symbols-outlined text-[16px]">
                  refresh
                </span>
                Refresh
              </button>
              <button class="icon-ghost ml-1" type="button" @click="closeModals">
                <span class="material-symbols-outlined text-[18px]">
                  close
                </span>
              </button>
            </div>
          </div>
          <div class="p-4 sm:p-5 max-h-[70vh] overflow-y-auto">
            <transition-group name="list" tag="div"
              class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              <label v-for="f in folderItems[activeFolder] || []" :key="f.id || f.url"
                class="rounded-2xl border border-slate-800/80 overflow-hidden bg-slate-950/80 cursor-pointer group/item flex flex-col">
                <div class="h-32 w-full relative">
                  <div v-if="isImage(f.filename)"
                    class="h-full w-full bg-center bg-cover transition-transform duration-300 group-hover/item:scale-105"
                    :style="{ backgroundImage: `url(${f.url})` }"></div>
                  <div v-else-if="isFont(f.filename)" class="h-full w-full grid place-items-center bg-slate-900">
                    <div
                      class="text-[11px] text-slate-100 px-2 py-1 rounded-full bg-slate-800/90 border border-slate-700">
                      OTF file
                    </div>
                  </div>
                  <div v-else class="h-full w-full grid place-items-center bg-slate-900">
                    <span class="material-symbols-outlined text-4xl text-slate-500">
                      description
                    </span>
                  </div>
                </div>
                <div class="p-2.5 text-[11px] text-slate-300 flex items-center justify-between gap-2 bg-slate-950/95">
                  <span class="truncate">
                    {{ f.filename || f.name || 'file' }}
                  </span>
                  <input type="checkbox" :value="f.url" v-model="selectedUrls"
                    class="h-3.5 w-3.5 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500 focus:ring-offset-0" />
                </div>
              </label>
            </transition-group>
            <div v-if="loading.items" class="mt-4 text-[11px] text-slate-400 flex items-center gap-2">
              <span class="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Loading files in folder
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="modals.create" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/55 backdrop-blur-sm" @click="closeCreate"></div>
        <div
          class="relative w-full max-w-md rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl shadow-2xl shadow-slate-950/60">
          <div
            class="px-5 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div class="space-y-0.5">
              <p class="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                New project
              </p>
              <h3 class="text-sm sm:text-base font-semibold">
                Palette project
              </h3>
            </div>
            <button class="icon-ghost" type="button" @click="closeCreate">
              <span class="material-symbols-outlined text-[18px]">
                close
              </span>
            </button>
          </div>
          <form class="p-5 sm:p-6 space-y-4" @submit.prevent="createProject">
            <div class="space-y-1.5">
              <label class="text-xs font-medium text-slate-700 dark:text-slate-200">
                Name
              </label>
              <input v-model.trim="newProject.name" type="text" placeholder="Neon studio explorations"
                class="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500/60" />
            </div>
            <div class="space-y-1.5">
              <label class="text-xs font-medium text-slate-700 dark:text-slate-200">
                Description
              </label>
              <textarea v-model.trim="newProject.description" rows="3"
                placeholder="Optional notes about what you are exploring with this palette."
                class="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 px-3.5 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500/60"></textarea>
            </div>
            <div class="flex items-center justify-between pt-2 text-[11px]">
              <span class="text-slate-500 dark:text-slate-400">
                You can refine details inside the project workspace.
              </span>
            </div>
            <div class="flex items-center justify-end gap-2 pt-2">
              <button type="button" class="chip" @click="closeCreate">
                <span class="material-symbols-outlined text-[16px]">
                  close
                </span>
                Cancel
              </button>
              <button :disabled="creating || !newProject.name" type="submit"
                class="inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white bg-slate-900 dark:bg-sky-500 shadow-md shadow-slate-900/20 dark:shadow-sky-500/30 hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:hover:translate-y-0">
                <span class="material-symbols-outlined text-[18px]">
                  add
                </span>
                {{ creating ? 'Creating…' : 'Create project' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const API = 'https://backend.nexgridco.com'
const api = axios.create({ baseURL: API })
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token')
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

const projects = ref([])
const folders = ref([])
const folderItems = reactive({})
const loading = reactive({ projects: false, folders: false, items: false })
const displayedFolders = computed(() => folders.value.slice(0, 6))
const heroScroller = ref(null)

function scrollHero(dir = 1) {
  heroScroller.value?.scrollBy({ left: dir * 344, behavior: 'smooth' })
}

function pickStyle() {
  return { bg: 'from-sky-500 via-sky-600 to-sky-800', glow: '#0ea5e9' }
}

function getPreviewColors(project) {
  if (Array.isArray(project.previewColors) && project.previewColors.length) {
    return project.previewColors.slice(0, 5)
  }
  return ['#104E43', '#8CC63F', '#EC5E10', '#FAA41F', '#FFE5B3']
}

const heroProjects = computed(() => {
  const arr = [...projects.value].sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  )
  return arr.slice(0, 14).map((p) => {
    const style = pickStyle()
    const count =
      p._count?.palettes ??
      p.palettesCount ??
      (Array.isArray(p.palettes) ? p.palettes.length : 0)
    return {
      id: p.id,
      name: p.name || 'Project',
      when: formatAgo(p.updatedAt || p.createdAt),
      bg: style.bg,
      glow: style.glow,
      count,
      previewColors: getPreviewColors(p)
    }
  })
})

function isImage(filename = '') {
  return /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(filename)
}

function isFont(filename = '') {
  return /\.otf$/i.test(filename)
}

function formatAgo(d) {
  const t = new Date(d).getTime()
  if (!t) return '—'
  const s = Math.floor((Date.now() - t) / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const days = Math.floor(h / 24)
  if (days < 7) return `${days}d ago`
  return new Date(t).toLocaleDateString()
}

function goOpenProject(id) {
  router.push({ name: 'studio-project', params: { projectId: id } })
}

const modals = reactive({ folder: false, create: false })
const newProject = reactive({ name: '', description: '' })
const creating = ref(false)

function openCreateProject() {
  newProject.name = ''
  newProject.description = ''
  modals.create = true
}

function closeCreate() {
  modals.create = false
}

async function createProject() {
  if (!newProject.name || creating.value) return
  creating.value = true
  try {
    const payload = {
      name: newProject.name.trim(),
      description: newProject.description?.trim() || undefined
    }
    const { data } = await api.post('/studio/projects', payload)
    const id = data?.id
    await loadProjects()
    modals.create = false
    if (id) {
      router.push({ name: 'studio-project', params: { projectId: id } })
    }
  } finally {
    creating.value = false
  }
}

function openExplorer() {
  router.push({ name: 'galeria' })
}

const activeFolder = ref('')
const selectedUrls = ref([])

function openFolder(name) {
  activeFolder.value = name
  selectedUrls.value = []
  modals.folder = true
  loadFolderFiles(name)
}

function closeModals() {
  modals.folder = false
}

function openFolderGallery(name) {
  router.push({ name: 'galeria', params: { gid: name || '' } })
}

function selectAllInFolder() {
  const items = folderItems[activeFolder.value] || []
  selectedUrls.value = items.map((i) => i.url)
}

function clearSelection() {
  selectedUrls.value = []
}

async function copySelectedUrls() {
  const t = (selectedUrls.value || []).join('\n')
  if (!t) return
  await navigator.clipboard.writeText(t)
}

async function copyFolderUrlsQuick(name) {
  if (!folderItems[name] || !folderItems[name].length) {
    await loadFolderFiles(name)
  }
  const arr = (folderItems[name] || []).map((i) => i.url).join('\n')
  if (!arr) return
  await navigator.clipboard.writeText(arr)
}

async function loadProjects() {
  try {
    loading.projects = true
    const { data } = await api.get('/studio/projects')
    projects.value = Array.isArray(data) ? data : []
  } finally {
    loading.projects = false
  }
}

async function refreshFolders() {
  try {
    loading.folders = true
    const res = await api.get('/images/groups')
    const list = Array.isArray(res.data?.groups)
      ? res.data.groups
      : Array.isArray(res.data)
        ? res.data
        : []
    folders.value = list.map((g) => ({
      name: g.name || g,
      count: g.count || 0
    }))
  } finally {
    loading.folders = false
  }
}

async function loadFolderFiles(name) {
  if (!name) return
  try {
    loading.items = true
    const { data } = await api.get('/images/list', {
      params: { group: name, skip: 0, take: 96, q: '', sort: 'new' }
    })
    const arr = Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : []
    folderItems[name] = arr.map((x) => ({
      id: x.id || x._id || x.url || x.filename,
      filename: x.filename || x.name,
      url: x.absoluteUrl || x.url
    }))
  } finally {
    loading.items = false
  }
}

function folderPreviewGradient(name) {
  const base = name || 'root'
  const R = seeded(base)
  const colors = []
  for (let i = 0; i < 3; i++) {
    colors.push(
      hslToHex(
        Math.floor(R() * 360),
        52 + Math.floor(R() * 22),
        48 + Math.floor(R() * 16)
      )
    )
  }
  return `linear-gradient(135deg, ${colors.join(', ')})`
}

function seeded(t) {
  let s = 0
  for (let i = 0; i < t.length; i++) s = (s * 31 + t.charCodeAt(i)) >>> 0
  return () =>
    ((s = (s * 1664525 + 1013904223) >>> 0), s / 0xffffffff)
}

function hslToHex(h, s, l) {
  s /= 100
  l /= 100
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (h >= 0 && h < 60) {
    r = c
    g = x
    b = 0
  } else if (h >= 60 && h < 120) {
    r = x
    g = c
    b = 0
  } else if (h >= 120 && h < 180) {
    r = 0
    g = c
    b = x
  } else if (h >= 180 && h < 240) {
    r = 0
    g = x
    b = c
  } else if (h >= 240 && h < 300) {
    r = x
    g = 0
    b = c
  } else {
    r = c
    g = 0
    b = x
  }
  const toHex = (v) =>
    ('0' + Math.round((v + m) * 255).toString(16)).slice(-2)
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

const firstName = computed(() => {
  const userStr =
    localStorage.getItem('user') || sessionStorage.getItem('user') || ''
  let name = ''
  if (userStr) {
    try {
      const u = JSON.parse(userStr)
      name = u?.name || u?.username || u?.email || ''
    } catch {
      //
    }
  }
  if (!name)
    name =
      localStorage.getItem('name') || sessionStorage.getItem('name') || ''
  if (!name)
    name =
      (localStorage.getItem('email') ||
        sessionStorage.getItem('email') ||
        'User'
      ).split('@')[0]
  name = String(name).trim()
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : 'User'
})

onMounted(async () => {
  await Promise.all([loadProjects(), refreshFolders()])
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

.font-sans {
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont,
    'SF Pro Text', sans-serif;
}

@keyframes aurora {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.card-glow-container {
  position: relative;
}

.card-glow {
  position: relative;
  width: 100%;
  height: 11rem;
  border-radius: 1.25rem;
  overflow: hidden;
  text-align: left;
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  transform: perspective(1100px) translateZ(0);
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.25);
}

.card-glow:hover {
  transform: perspective(1100px) translateZ(0) scale(1.03) rotateX(2deg) rotateY(-2deg);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.4);
}

.card-glow:active {
  transform: perspective(1100px) translateZ(0) scale(1.01) rotateX(1deg) rotateY(-1deg);
}

.card-glow::before {
  content: '';
  position: absolute;
  inset: -20%;
  background: radial-gradient(circle, var(--glow-color) 0%, transparent 60%);
  opacity: 0.18;
  filter: blur(42px);
  transition: opacity 0.45s ease;
}

.dark .card-glow::before {
  opacity: 0.3;
}

.card-glow:hover::before {
  opacity: 0.4;
}

.dark .card-glow:hover::before {
  opacity: 0.6;
}

.shadow-apple {
  box-shadow: 0 18px 55px rgba(15, 23, 42, 0.35);
}

.card-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(to bottom right, #0ea5e9, #0369a1);
  background-size: 180% 180%;
  background-position: 50% 50%;
  transition: background-position 0.8s ease;
}

.card-glow:hover .card-bg {
  background-position: 0% 0%;
}

.card-content {
  position: relative;
  z-index: 1;
  height: 100%;
  width: 100%;
  padding: 1.15rem 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #f9fafb;
  background: linear-gradient(to top,
      rgba(15, 23, 42, 0.78),
      rgba(15, 23, 42, 0.12));
}

.chip {
  @apply inline-flex items-center justify-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-full border border-slate-700/70 bg-slate-900/60 text-slate-100 hover:bg-slate-800/80 transition-colors disabled:opacity-50 disabled:cursor-default;
}

.icon-ghost {
  @apply inline-flex items-center justify-center rounded-full bg-slate-100/70 dark:bg-slate-900/80 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700/80 hover:bg-slate-200/90 dark:hover:bg-slate-800 transition-colors;
}

.icon-ghost>.material-symbols-outlined {
  font-size: 18px;
  line-height: 1;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(14px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

.fancy-scroll::-webkit-scrollbar {
  height: 8px;
}

.fancy-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.fancy-scroll::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  background-clip: padding-box;
  border: 2px solid transparent;
  background-color: rgba(148, 163, 184, 0.8);
}

.dark .fancy-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(51, 65, 85, 0.95);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.26s ease;
}

.modal-enter-active>div:not(.absolute),
.modal-leave-active>div:not(.absolute) {
  transition: transform 0.26s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from>div:not(.absolute),
.modal-leave-to>div:not(.absolute) {
  transform: translateY(10px) scale(0.97);
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.35s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(18px);
}

.list-leave-active {
  position: absolute;
}
</style>
